import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { Link, useParams } from "react-router-dom";
import { ArrowLeft } from "lucide-react";
import { useApi } from "@/lib/api";
import { useTrack } from "@/lib/track";
import { cn } from "@/lib/cn";
import { Section } from "@/components/intel/Section";
import { ItemCard, ItemCardEmpty, type FeedItem } from "@/components/intel/ItemCard";
import { OpportunityCard, type Opportunity } from "@/components/opps/OpportunityCard";

type EntryState = "unread" | "revisiting" | "archived";

interface IntelEntry extends FeedItem {
  entry_id: string;
  state: EntryState;
  added_at: number;
  last_revisited_at: number | null;
}

interface OppEntry extends Opportunity {
  entry_id: string;
  state: EntryState;
  added_at: number;
  last_revisited_at: number | null;
}

interface BriefEntry {
  entry_id: string;
  state: EntryState;
  added_at: number;
  last_revisited_at: number | null;
  id: string;
  kind: string;
  title: string;
  generated_at: number;
}

interface CollectionPayload {
  collection: {
    id: string;
    name: string;
    slug: string;
    description: string | null;
    created_at: number;
  };
  intelligence: IntelEntry[];
  opportunities: OppEntry[];
  briefings: BriefEntry[];
}

const DAY_MS = 24 * 60 * 60 * 1000;
const REVISIT_AFTER_DAYS = 30;

/**
 * Marks an entry "worth revisiting" if it's stale: added >30d ago AND
 * either never revisited or last revisit also >30d ago. Helps the user
 * notice things they kept but haven't returned to.
 */
function worthRevisiting(added_at: number, last_revisited_at: number | null): boolean {
  const ageDays = (Date.now() - added_at) / DAY_MS;
  if (ageDays < REVISIT_AFTER_DAYS) return false;
  if (!last_revisited_at) return true;
  const sinceRevisit = (Date.now() - last_revisited_at) / DAY_MS;
  return sinceRevisit > REVISIT_AFTER_DAYS;
}

function daysAgo(ts: number): string {
  const d = Math.round((Date.now() - ts) / DAY_MS);
  if (d < 1) return "today";
  if (d === 1) return "yesterday";
  if (d < 30) return `${d} days ago`;
  if (d < 365) return `${Math.round(d / 30)}mo ago`;
  return `${Math.round(d / 365)}y ago`;
}

export function CollectionDetail() {
  const { slug = "" } = useParams();
  const api = useApi();
  const qc = useQueryClient();
  const track = useTrack();

  const detail = useQuery({
    queryKey: ["collection", slug],
    queryFn: () => api.get<CollectionPayload>(`/collections/${encodeURIComponent(slug)}`),
    enabled: slug.length > 0,
  });

  const patch = useMutation({
    mutationFn: ({
      collectionId,
      entryId,
      body,
    }: {
      collectionId: string;
      entryId: string;
      body: Record<string, unknown>;
    }) => api.patch(`/collections/${collectionId}/entries/${entryId}`, body),
    onSuccess: () => qc.invalidateQueries({ queryKey: ["collection", slug] }),
  });

  if (detail.isLoading) {
    return (
      <div className="px-10 py-10 max-w-5xl">
        <ItemCardEmpty label="Reading the collection…" />
      </div>
    );
  }
  if (detail.isError || !detail.data) {
    return (
      <div className="px-10 py-10 max-w-5xl">
        <Link
          to="/app/library"
          className="inline-flex items-center gap-2 text-xs uppercase tracking-[0.18em] text-whisper hover:text-gold-glow"
        >
          <ArrowLeft className="size-3" strokeWidth={1.5} />
          Back to Library
        </Link>
        <div className="mt-8">
          <ItemCardEmpty label="Couldn't load this collection." />
        </div>
      </div>
    );
  }

  const { collection, intelligence, opportunities, briefings } = detail.data;
  const totalEntries = intelligence.length + opportunities.length + briefings.length;
  const totalArchived =
    intelligence.filter((e) => e.state === "archived").length +
    opportunities.filter((e) => e.state === "archived").length +
    briefings.filter((e) => e.state === "archived").length;

  const setState = (entryId: string, newState: EntryState) => {
    patch.mutate({
      collectionId: collection.id,
      entryId,
      body: {
        state: newState,
        mark_revisited: newState === "revisiting",
      },
    });
    if (newState === "revisiting") track("collection.entry.revisit", { kind: collection.slug });
    if (newState === "archived") track("collection.entry.archive", { kind: collection.slug });
  };

  return (
    <div className="px-10 py-10 max-w-5xl">
      <Link
        to="/app/library"
        className="inline-flex items-center gap-2 text-xs uppercase tracking-[0.18em] text-whisper hover:text-gold-glow"
      >
        <ArrowLeft className="size-3" strokeWidth={1.5} />
        Back to Library
      </Link>

      <header className="mt-6 mb-10">
        <p className="label-eyebrow">Collection</p>
        <h1 className="mt-2 text-display-2 font-display">{collection.name}</h1>
        {collection.description && (
          <p className="mt-3 text-whisper max-w-2xl leading-relaxed">
            {collection.description}
          </p>
        )}
        <p className="mt-3 text-[10px] uppercase tracking-[0.18em] text-muted">
          {totalEntries} entr{totalEntries === 1 ? "y" : "ies"}
          {totalArchived > 0 && ` · ${totalArchived} archived`} · started {daysAgo(collection.created_at)}
        </p>
      </header>

      {totalEntries === 0 && (
        <ItemCardEmpty
          label="No entries yet."
          detail="Add intelligence, opportunities, or briefings from anywhere in the app — they'll land here."
        />
      )}

      {intelligence.length > 0 && (
        <Section eyebrow="Saved" title="Intelligence" detail={`${intelligence.length} signal${intelligence.length === 1 ? "" : "s"}`}>
          <div className="grid gap-4">
            {intelligence.map((e) => (
              <EntryWrapper
                key={e.entry_id}
                addedAt={e.added_at}
                lastRevisitedAt={e.last_revisited_at}
                state={e.state}
                onRevisit={() => setState(e.entry_id, "revisiting")}
                onArchive={() => setState(e.entry_id, e.state === "archived" ? "unread" : "archived")}
                busy={patch.isPending}
              >
                <ItemCard item={e} variant="full" isSaved />
              </EntryWrapper>
            ))}
          </div>
        </Section>
      )}

      {opportunities.length > 0 && (
        <Section eyebrow="Saved" title="Opportunities" detail={`${opportunities.length} kept`}>
          <div className="grid gap-4">
            {opportunities.map((e) => (
              <EntryWrapper
                key={e.entry_id}
                addedAt={e.added_at}
                lastRevisitedAt={e.last_revisited_at}
                state={e.state}
                onRevisit={() => setState(e.entry_id, "revisiting")}
                onArchive={() => setState(e.entry_id, e.state === "archived" ? "unread" : "archived")}
                busy={patch.isPending}
              >
                <OpportunityCard opp={e} variant="full" />
              </EntryWrapper>
            ))}
          </div>
        </Section>
      )}

      {briefings.length > 0 && (
        <Section eyebrow="Saved" title="Briefings" detail={`${briefings.length} kept`}>
          <ul className="panel divide-y divide-veil/40">
            {briefings.map((e) => (
              <li key={e.entry_id}>
                <EntryWrapper
                  addedAt={e.added_at}
                  lastRevisitedAt={e.last_revisited_at}
                  state={e.state}
                  onRevisit={() => setState(e.entry_id, "revisiting")}
                  onArchive={() => setState(e.entry_id, e.state === "archived" ? "unread" : "archived")}
                  busy={patch.isPending}
                  variant="row"
                >
                  <Link
                    to={`/app/briefings`}
                    className="block py-4 hover:text-gold-glow transition-colors"
                  >
                    <div className="text-[10px] uppercase tracking-[0.18em] text-muted">
                      {e.kind} · {new Date(e.generated_at).toLocaleDateString(undefined, { month: "short", day: "numeric", year: "numeric" })}
                    </div>
                    <div className="mt-1 font-display text-base">{e.title}</div>
                  </Link>
                </EntryWrapper>
              </li>
            ))}
          </ul>
        </Section>
      )}
    </div>
  );
}

function EntryWrapper({
  children,
  addedAt,
  lastRevisitedAt,
  state,
  onRevisit,
  onArchive,
  busy,
  variant = "card",
}: {
  children: React.ReactNode;
  addedAt: number;
  lastRevisitedAt: number | null;
  state: EntryState;
  onRevisit: () => void;
  onArchive: () => void;
  busy: boolean;
  variant?: "card" | "row";
}) {
  const stale = worthRevisiting(addedAt, lastRevisitedAt);
  return (
    <div className={cn(state === "archived" && "opacity-60")}>
      <div className="mb-2 flex items-center justify-between gap-4">
        <div className="text-[10px] uppercase tracking-[0.18em] text-muted">
          Added {daysAgo(addedAt)}
          {lastRevisitedAt && <span> · revisited {daysAgo(lastRevisitedAt)}</span>}
          {stale && state !== "archived" && (
            <span className="ml-3 text-gold-glow/80">Worth revisiting</span>
          )}
          {state === "archived" && <span className="ml-3 text-muted">Archived</span>}
        </div>
        <div className={cn(
          "flex items-center gap-3 text-[10px] uppercase tracking-[0.18em] text-whisper",
          variant === "row" && "pr-6"
        )}>
          {state !== "archived" && (
            <button
              type="button"
              onClick={onRevisit}
              disabled={busy}
              className="hover:text-fog disabled:opacity-40"
            >
              Revisit
            </button>
          )}
          <button
            type="button"
            onClick={onArchive}
            disabled={busy}
            className="hover:text-fog disabled:opacity-40"
          >
            {state === "archived" ? "Restore" : "Archive"}
          </button>
        </div>
      </div>
      <div className={variant === "row" ? "px-6" : ""}>{children}</div>
    </div>
  );
}
